import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, IconButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar, DeleteConfirmationDialog } from '@/components';
import EditorSidebar from '@/components/EditorSidebar';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import OrganisationEditor from '@features/user-management/components/organisations/OrganisationEditor';
import { useNotifications } from '@core/hooks/useNotifications';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage';
import { Organisation, OrganisationInput, organisationService } from '@/services/organisationService';
import { DateTime } from 'luxon';
import { useAuthStore } from '@core/store';
import { authService } from '@core/auth/AuthService.ts';

const Organisations: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();
    const { updateOrganisationClaim } = useAuthStore();

    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [selected, setSelected] = useState<Organisation | null>(null);
    const [itemToDelete, setItemToDelete] = useState<Organisation | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const load = async () => {
        setLoading(true);
        try {
            const data = await organisationService.getOrganisationsForDisplay();
            setOrganisations(data || []);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('Failed to load organisations:', error);
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    useRegisterRefresh(load, loading);

    const handleAdd = () => {
        const newOrg: Organisation = {
            name: '',
            activeLicenses: []
        };
        setSelected(newOrg);
    };

    const saveItem = useCallback(
        async (item: OrganisationInput) => {
            if (isSaving) return;

            const validation = organisationService.validateOrganisation(item);
            if (!validation.valid) {
                validation.errors.forEach((error) => notifyErrorResponse(new Error(error)));
                return;
            }

            setIsSaving(true);

            try {
                if (item?.id) {
                    await organisationService.updateOrganisation(item);
                    updateOrganisationClaim({
                        id: item.id,
                        name: item.name,
                        activeLicenses: item.activeLicenses ?? []
                    });
                } else {
                    await organisationService.createOrganisation(item);
                    await authService.refreshUserClaims();
                }

                notifySuccess();
                setSelected(null);
                load();
            } catch (error) {
                console.error('Failed to save organisation:', error);
                notifyErrorResponse(error);
            } finally {
                setIsSaving(false);
            }
        },
        [notifySuccess, notifyErrorResponse]
    );

    const askDelete = (item: Organisation) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const deleteItem = async () => {
        if (!itemToDelete?.id) return;

        try {
            await organisationService.deleteOrganisation(itemToDelete.id);
            notifySuccess();
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setSelected(null);
            load();
        } catch (error) {
            console.error('Failed to delete organisation:', error);
            notifyErrorResponse(error);
        }
    };

    const columns: DataTableColumn<Organisation>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 200
            },
            {
                text: t('licenses'),
                value: 'licensesDisplay',
                width: 400
            },
            {
                text: t('users'),
                value: 'usersDisplay',
                align: 'center',
                width: 200
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                sortable: false,
                align: 'center',
                isAction: true,
                render: (organisation) => (
                    <Box className="organisations-table-actions" sx={{ justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => setSelected(organisation)}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton size="small" onClick={() => askDelete(organisation)}>
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [t]
    );

    const renderContent = () => {
        if (loading) {
            return (
                <Box className="organisations-loading">
                    <LoadingSpinner showBackground={false} />
                </Box>
            );
        }

        return (
            <NewWcnTable
                columns={columns}
                data={organisations}
                loading={loading}
                noDataMessage={t('noData')}
                size="small"
                sorting={{ defaultSortBy: 'name' }}
            />
        );
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <Box className="organisations-page">
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-account" style={{ fontSize: '16px' }} />}
                title={t('organisations')}
                menus={getMenuData()}
                currentRoute={{ path: '/manage-organisation/organisations' }}
                iconSize="21px"
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 4, pb: 1, '@media (max-width: 480px)': { pt: 3 } }}>
                <Button
                    variant="text"
                    onClick={handleAdd}
                    disabled={loading}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i
                            className="bi bi-plus-lg"
                            style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                        />
                    </Box>
                    <span>{t('add')}</span>
                </Button>
            </Box>

            <Box className="organisations-page-content">{renderContent()}</Box>

            <EditorSidebar open={!!selected} onClose={() => setSelected(null)}>
                {selected && (
                    <OrganisationEditor
                        item={selected}
                        isSaving={isSaving}
                        onSave={saveItem}
                        onDelete={askDelete}
                        onCancel={() => setSelected(null)}
                    />
                )}
            </EditorSidebar>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteMessage', { item: t('organisation') })}
                onConfirm={deleteItem}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setItemToDelete(null);
                }}
            />
        </Box>
    );
};

Organisations.displayName = 'Organisations';
Organisations.description = 'Manage organisations in the system';

export default Organisations;
